/* ============================================================
   Wiredan App - GlobalBanner.jsx (Clean Top Banner)
   Path: src/components/GlobalBanner.jsx
   ============================================================ */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiMessageSquare, FiX, FiExternalLink, 
  FiZap 
} from 'react-icons/fi';
import GlobalChatBanner from './GlobalChatBanner';
import { useAuth } from '../context/AuthContext';

const DAN_CONTRACT = "0xb0040f8b957329bfb2615dc081c1666540A153bd";
const SIDRA_EXPLORE_BASE = "https://dex.sidrachain.com/#/explore/tokens/sidra";

const GlobalBanner = () => {
    const { user, token: authToken } = useAuth();
    const [visible, setVisible] = useState(true);
    const [isGlobalChatOpen, setIsGlobalChatOpen] = useState(false);
    const navigate = useNavigate();

    // Token configuration (price elements removed while retaining DEX link logic)
    const [tokens] = useState([
        {
            symbol: 'DAN',
            name: 'D Agribusiness Network',
            address: DAN_CONTRACT
        }
    ]);

    const isPolling = false;

    if (!visible) return null;

    const openSidraDexExplorer = (address) => {
        const targetAddress = address || DAN_CONTRACT;
        window.open(`${SIDRA_EXPLORE_BASE}/${targetAddress}`, '_blank', 'noopener,noreferrer');
    };

    const handleOpenChat = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (user?.email && authToken) {
            try {
                await fetch('/api/chat/global', {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${authToken}`, 
                        'Content-Type': 'application/json' 
                    },
                    body: JSON.stringify({
                        action: 'create_thread',
                        senderId: user.email,
                        recipientId: "mtlhumaid@gmail.com"
                    })
                });
            } catch (err) {
                console.error("Auto thread creation error:", err);
            }
        }
        setIsGlobalChatOpen(true);
    };

    const activeAddress = tokens[0]?.address || DAN_CONTRACT;

    return (
      <>
        <div className="bg-[#070C14]/95 text-white px-2 py-1 flex items-center justify-between z-[100] sticky top-0 border-b border-[#F25C05]/30 shadow-2xl backdrop-blur-md font-sans">
            
            {/* LEFT CONTAINER: Status Indicator & DAN Link */}
            <div className="flex items-center space-x-1.5 sm:space-x-2 overflow-x-auto no-scrollbar py-0.5 min-w-0">
                
                {/* Live Indicator */}
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg bg-gradient-to-r from-emerald-950/90 to-slate-900/90 border border-emerald-500/40 shrink-0 shadow-sm">
                    <span className="relative flex h-2 w-2 items-center justify-center">
                        <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
                            isPolling ? 'bg-amber-400 animate-ping' : 'bg-emerald-400 animate-ping'
                        }`} />
                        <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                            isPolling ? 'bg-amber-400' : 'bg-emerald-400'
                        }`} />
                    </span>
                    <span className={`text-[8px] font-mono font-black tracking-widest uppercase ${
                        isPolling ? 'text-amber-400' : 'text-emerald-400'
                    }`}>
                        {isPolling ? 'SYNC' : 'LIVE'}
                    </span>
                </div>

                {/* DAN Token Link */}
                {tokens.map((token) => {
                    const targetAddress = token.address || DAN_CONTRACT;
                    return (
                        <div 
                            key={targetAddress} 
                            onClick={() => openSidraDexExplorer(targetAddress)}
                            className="flex items-center space-x-1 bg-[#0B1221]/90 hover:bg-[#0D1627] px-2 py-0.5 rounded-lg border border-slate-800 hover:border-[#F25C05]/50 cursor-pointer transition-all duration-200 shrink-0"
                            title="View DAN on Sidra DEX"
                        >
                            <span className="font-extrabold text-[#F25C05] uppercase text-[10px] tracking-tight">
                                {token.symbol}
                            </span>
                            <div className="flex items-center gap-0.5 pl-1 border-l border-slate-800 text-[#F25C05] hover:text-amber-400 text-[8px] font-bold uppercase">
                                <span>DEX</span>
                                <FiExternalLink size={7} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* RIGHT CONTAINER: Action Buttons */}
            <div className="flex items-center space-x-1 pl-1 border-l border-slate-800/80 shrink-0">
                
                {/* Public DEX Terminal Button */}
                <button 
                    type="button"
                    title="Open Official Sidra DEX Explorer for DAN"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openSidraDexExplorer(activeAddress);
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 hover:from-[#F25C05] hover:to-amber-600 text-white border border-slate-700 hover:border-orange-400/40 transition-all duration-200 font-bold text-[8px] uppercase tracking-wider cursor-pointer shrink-0"
                >
                    <FiZap size={10} className="text-amber-400" />
                    <span>TERMINAL</span>
                    <FiExternalLink size={8} />
                </button>

                {/* Chat Launcher */}
                <button
                    type="button"
                    onClick={handleOpenChat}
                    className="flex items-center gap-1 bg-slate-800/80 hover:bg-slate-700 text-slate-300 p-1.5 rounded-lg text-[8px] font-bold uppercase transition-colors cursor-pointer shrink-0"
                    title="Open Chat Node"
                >
                    <FiMessageSquare size={10} />
                </button>

                {/* Close Banner */}
                <button 
                    type="button"
                    title="Close Banner"
                    onClick={() => setVisible(false)} 
                    className="text-slate-400 hover:text-rose-400 p-1 rounded-lg transition-colors cursor-pointer shrink-0"
                >
                    <FiX size={13} />
                </button>
            </div>
        </div>

        {/* Global Chat Integration Component */}
        <GlobalChatBanner externalOpen={isGlobalChatOpen} onToggleExternal={(val) => setIsGlobalChatOpen(val)} />
      </>
    );
};

export default GlobalBanner;